import React, { useState, useEffect } from 'react';
import { settingsApi } from '../services/api';
import { PageHead, Card, Btn, Spinner, Toast } from '../components/UI';

export default function SettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [edited,   setEdited]   = useState({});
  const [toast,    setToast]    = useState(null);

  useEffect(() => {
    settingsApi.getAll()
      .then(r => setSettings(r.data.data || r.data || []))
      .catch(() => setSettings(DEMO_SETTINGS))
      .finally(() => setLoading(false));
  }, []);

  function handleChange(key, value) {
    setEdited(e => ({ ...e, [key]: value }));
  }

  async function handleSaveAll() {
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(edited).map(([key, value]) => settingsApi.update(key, value))
      );
      setToast({ msg: 'All settings saved successfully.', type:'success' });
      setEdited({});
      // Refresh
      const res = await settingsApi.getAll();
      setSettings(res.data.data || res.data || []);
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Some settings failed to save.', type:'error' });
    } finally { setSaving(false); }
  }

  function resetEdits() { setEdited({}); }

  const hasChanges = Object.keys(edited).length > 0;

  const grouping = [
    { label:'🔐 Authentication', keys:['OTP_ExpiryMinutes','OTP_MaxAttempts','TrustedDevice_ExpiryDays','School_EmailDomain','Session_ExpiryMinutes'] },
  { label:'📅 Booking LE Rules',  keys:['Booking_SlotStartHour','Booking_SlotEndHour','Booking_SlotIntervalMinutes'] },
  ];

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:'4rem', gap:10 }}>
      <Spinner size={24}/><span style={{ color:'var(--text2)' }}>Loading settings…</span>
    </div>
  );

  return (
    <div className="page-anim">
      <PageHead
        title="System Settings"
        subtitle="Configure application-wide parameters"
        actions={
          <div style={{ display:'flex', gap:8 }}>
            {hasChanges && <Btn variant="ghost" onClick={resetEdits}>↩️ Reset</Btn>}
            <Btn variant="primary" onClick={handleSaveAll} disabled={saving || !hasChanges}>
              {saving ? <Spinner size={16} color="white"/> : '💾 Save All Changes'}
            </Btn>
          </div>
        }
      />

      {grouping.map(g => {
        const groupSettings = settings.filter(s => g.keys.includes(s.settingKey));
        if (!groupSettings.length) return null;
        return (
          <div key={g.label} style={{ marginBottom:'1.6rem' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--text2)', marginBottom:'1rem' }}>{g.label}</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'1rem' }}>
              {groupSettings.map(s => {
                const current = edited[s.settingKey] ?? s.settingValue;
                const changed = edited[s.settingKey] !== undefined && edited[s.settingKey] !== s.settingValue;
                return (
                  <Card key={s.settingKey} style={{ border: changed ? '1.5px solid var(--blue)' : undefined }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>{s.settingKey}</div>
                    <div style={{ fontFamily:'var(--font-head)', fontSize:14, fontWeight:700, marginBottom:8 }}>{friendlyLabel(s.settingKey)}</div>
                    <input value={current} onChange={e => handleChange(s.settingKey, e.target.value)}
                      style={{ padding:'10px 14px', border:`1.5px solid ${changed ? 'var(--blue)' : 'var(--border)'}`, borderRadius:8, fontSize:14, fontFamily:'var(--font-body)', outline:'none', width:'100%', background: changed ? 'var(--blue-light)' : 'white', transition:'all .15s' }} />
                    {s.description && <div style={{ fontSize:11, color:'var(--text3)', marginTop:6 }}>{s.description}</div>}
                    {changed && <div style={{ fontSize:11, color:'var(--blue)', marginTop:4, fontWeight:600 }}>⚡ Modified</div>}
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Any ungrouped settings */}
      {(() => {
        const allGroupedKeys = grouping.flatMap(g => g.keys);
        const ungrouped = settings.filter(s => !allGroupedKeys.includes(s.settingKey));
        if (!ungrouped.length) return null;
        return (
          <div style={{ marginBottom:'1.6rem' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--text2)', marginBottom:'1rem' }}>⚙️ Other Settings</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'1rem' }}>
              {ungrouped.map(s => {
                const current = edited[s.settingKey] ?? s.settingValue;
                const changed = edited[s.settingKey] !== undefined;
                return (
                  <Card key={s.settingKey}>
                    <div style={{ fontSize:10, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>{s.settingKey}</div>
                    <div style={{ fontFamily:'var(--font-head)', fontSize:14, fontWeight:700, marginBottom:8 }}>{friendlyLabel(s.settingKey)}</div>
                    <input value={current} onChange={e => handleChange(s.settingKey, e.target.value)}
                      style={{ padding:'10px 14px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:14, fontFamily:'var(--font-body)', outline:'none', width:'100%' }} />
                    {s.description && <div style={{ fontSize:11, color:'var(--text3)', marginTop:6 }}>{s.description}</div>}
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })()}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function friendlyLabel(key) {
  const map = {
    OTP_ExpiryMinutes:          'OTP Expiry (minutes)',
    OTP_MaxAttempts:            'OTP Max Attempts',
    TrustedDevice_ExpiryDays:   'Trusted Device Expiry (days)',
    School_EmailDomain:         'Allowed Email Domain',
    Session_ExpiryMinutes:      'Session Expiry (minutes)',
    Booking_SlotStartHour:      'Slot Start Hour (24h)',
    Booking_SlotEndHour:        'Slot End Hour (24h)',
    Booking_SlotIntervalMinutes:'Slot Interval (minutes)',
  };
  return map[key] || key.replace(/_/g,' ');
}

const DEMO_SETTINGS = [
  { settingKey:'OTP_ExpiryMinutes',        settingValue:'10',                    description:'OTP validity in minutes' },
  { settingKey:'OTP_MaxAttempts',          settingValue:'5',                     description:'Max wrong OTP attempts before lockout' },
  { settingKey:'TrustedDevice_ExpiryDays', settingValue:'30',                    description:'Trusted device validity in days' },
  { settingKey:'Booking_SlotStartHour',    settingValue:'8',                     description:'Earliest bookable hour (24h)' },
  { settingKey:'Booking_SlotEndHour',      settingValue:'22',                    description:'Latest bookable end hour (24h)' },
  { settingKey:'Booking_SlotIntervalMinutes', settingValue:'30',                 description:'Booking slot interval in minutes' },
  { settingKey:'Session_ExpiryMinutes',    settingValue:'480',                   description:'JWT session validity in minutes (8 hrs)' },
  { settingKey:'School_EmailDomain',       settingValue:'elproschools.edu.in',   description:'Only this domain can register' },
];
